import React, { useMemo } from "react";
import { formatINR } from "../../utils/formatINR";
import { formatNumber, settlementSummary } from "../../utils/analyticsMetrics";
import { Skeleton } from "./AnalyticsUI";

const TILES = [
  { key: "settled", label: "Settled", tone: "text-green-700", dot: "bg-green-500", hint: "Paid out to station owners" },
  { key: "pending", label: "Pending", tone: "text-amber-700", dot: "bg-amber-500", hint: "Waiting to be settled" },
  { key: "processing", label: "Processing", tone: "text-brand-700", dot: "bg-brand-500", hint: "Settlement in progress" },
  { key: "failed", label: "Failed", tone: "text-red-700", dot: "bg-red-500", hint: "Settlement attempt failed" },
  { key: "skipped", label: "Skipped", tone: "text-neutral-700", dot: "bg-neutral-400", hint: "Not eligible for settlement" },
];

const SettlementSummary = ({ sessions, isLoading, available }) => {
  const summary = useMemo(() => settlementSummary(sessions), [sessions]);
  const tracked = TILES.reduce((a, t) => a + summary[t.key].count, 0);

  if (!isLoading && !available) {
    return (
      <div className="card p-5 text-13 text-neutral-600">
        Settlement data isn't available from the server yet. It appears here once the backend with
        <code className="mx-1 rounded bg-neutral-100 px-1 py-0.5 text-xs">cms_get_charging_sessions_with_settlement</code>
        is deployed.
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      <div className="grid grid-cols-2 divide-line sm:grid-cols-3 lg:grid-cols-5 lg:divide-x">
        {TILES.map((t) => (
          <div key={t.key} className="p-5">
            <p className="flex items-center gap-2 text-13 font-medium text-neutral-600">
              <span className={`h-2 w-2 rounded-full ${t.dot}`} />
              {t.label}
            </p>
            {isLoading ? (
              <Skeleton className="mt-2 h-7 w-28" />
            ) : (
              <p className={`mt-1.5 text-xl font-semibold tabular-nums ${t.tone}`}>{formatINR(summary[t.key].amount)}</p>
            )}
            <p className="mt-0.5 text-xs text-muted">
              {isLoading ? " " : `${formatNumber(summary[t.key].count)} sessions`} · {t.hint}
            </p>
          </div>
        ))}
      </div>
      {!isLoading && (
        <div className="border-t border-line bg-neutral-50 px-5 py-3 text-xs text-muted">
          Settlement amounts are what station owners receive after CMS charges.{" "}
          {formatNumber(tracked)} of {formatNumber(tracked + summary.none.count)} sessions in this period have a settlement record
          {summary.none.count > 0 ? `; ${formatNumber(summary.none.count)} sessions (${formatINR(summary.none.sessionValue)} billed) have none, e.g. groups without settlement enabled.` : "."}
        </div>
      )}
    </div>
  );
};

export default SettlementSummary;
